import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, ClipboardList, UserCircle, Plus } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { IS_BETA } from "@/version";

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const authed = user && typeof user === "object";

  const doLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const links = authed
    ? [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/reports", label: "My Reports", icon: ClipboardList },
        { to: "/account", label: "Account", icon: UserCircle },
      ]
    : [];

  return (
    <header className="sticky top-0 z-50 bg-navy text-white" data-testid="navbar">
      <nav className="container-mtr h-[56px] md:h-[60px] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo variant="dark" />
          {IS_BETA && (
            <span data-testid="beta-badge" className="text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange text-white leading-none">
              Beta
            </span>
          )}
        </div>

        {authed && (
          <div className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-[15px] font-semibold pb-1 border-b-[3px] transition-colors ${
                    isActive ? "text-white border-orange" : "text-mortar border-transparent hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        )}

        <div className="hidden md:flex items-center gap-3">
          {authed ? (
            <>
              <Link to="/dashboard" className="btn-primary !min-h-[36px] !py-2 !px-4 !text-[14px]" data-testid="nav-new-report">
                <Plus size={16} /> New Report
              </Link>
              <button onClick={doLogout} className="text-mortar hover:text-white" data-testid="nav-logout" aria-label="Log out">
                <LogOut size={20} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-[15px] font-semibold text-mortar hover:text-white" data-testid="nav-login">
                Log in
              </Link>
              <Link to="/signup" className="btn-primary !min-h-[40px] !py-2 !px-5" data-testid="nav-signup">
                Get started
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2.5 -mr-2 text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-navy border-t border-white/10 px-5 pb-5 pt-2 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {authed ? (
            <>
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="flex items-center gap-3 py-3 text-white font-semibold"
                >
                  <l.icon size={20} /> {l.label}
                </NavLink>
              ))}
              <button onClick={doLogout} className="flex items-center gap-3 py-3 text-mortar font-semibold" data-testid="nav-mobile-logout">
                <LogOut size={20} /> Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-white font-semibold" data-testid="nav-mobile-login">
                Log in
              </Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="btn-primary mt-2" data-testid="nav-mobile-signup">
                Get started
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export default Navbar;
